import { isError } from "@for-it/domain-lib";
import { Icon, useCurrentUser, useLogout, useNavigate } from "@for-it/web-lib";
import { UserRole } from "app-domain";
import { useEffect, useState, type PropsWithChildren } from "react";
import Footer from "../components/footer.tsx";
import IconContact from "../components/icon-contact.tsx";
import { Routes } from "../routes";
import { useUserStage } from "../utils/user-stage-hook.tsx";

interface MenuItem {
    label: string;
    route: string;
}

export default function HomeSidebar({ children }: PropsWithChildren) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [incompleteForm, setIncompleteFrom] = useState(false);
    const { userStage, logout } = useUserStage();
    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
    const currentUser = useCurrentUser();
    const navigate = useNavigate();

    const currentPath = window.location.pathname;

    const menuCompany: MenuItem[] = [
        {
            label: "Perfil empresarial",
            route: Routes.PROFILE_COMPANY.path(currentUser?.id),
        },
        {
            label: "Publicaciones",
            route: Routes.COMPANY_PUBLICATIONS.path(),
        },
        {
            label: "Postulantes",
            route: Routes.APPLICANTS.path(),
        },
    ];

    const menuAdmin: MenuItem[] = [
        { label: "Empresas", route: Routes.COMPANY_TABLE.path() },
        { label: "Postulantes", route: Routes.APPLICANTS_TABLE.path() },
        { label: "Certificaciones", route: Routes.CERTIFICATION_TABLE.path() },
        { label: "Publicaciones", route: Routes.ADMIN_PUBLICATIONS.path() },
        {
            label: "Cambiar contraseña",
            route: Routes.CHANGE_PASSWORD.path(),
        },
    ];

    const menuApplicant: MenuItem[] = [
        {
            label: "Mi Perfil",
            route: Routes.PROFILE_APPLICANT.path(currentUser?.id),
        },
        {
            label: "Mis Postulaciones",
            route: Routes.APPLICATIONS.path(),
        },
        {
            label: "Publicaciones",
            route: Routes.APPLICANT_PUBLICATIONS.path(),
        },
    ];

    const logOut = useLogout();

    async function onLogOut() {
        const result = await logOut();
        if (isError(result)) {
            return result;
        } else {
            logout();
            navigate.toRoute(Routes.LOGIN.path());
        }
    }

    const isActive = (route: string) => {
        return currentPath === route;
    };
    const verifyCompleteForm = () => {
        if (userStage && userStage == "INFO_COMPLETE") {
            setIncompleteFrom(true);
        }
    };

    useEffect(() => {
        verifyCompleteForm();
    }, [userStage]);

    return (
        <>
            {currentUser && incompleteForm ? (
                <div className="min-h-screen bg-gray-100">
                    <aside className="fixed top-0 left-0 h-full w-0 lg:w-64 bg-[#9C9C9A] text-white transition-all duration-300 z-20 overflow-hidden">
                        <div className="flex flex-col h-full">
                            <div className="p-5 transition-opacity duration-200 hover:opacity-80 cursor-pointer">
                                <img
                                    className="h-[60px] w-auto"
                                    src="https://www.saaf.org.ar/wp-content/uploads/2024/11/logosaaf.webp"
                                    alt="logo"
                                />
                            </div>
                            <nav className="mt-2 px-2 h-[78%]">
                                <ul className="space-y-1">
                                    {currentUser.role === UserRole.APPLICANT
                                        ? menuApplicant.map((menu, i) => (
                                              <li key={i}>
                                                  <a
                                                      href={menu.route}
                                                      className={`block p-3 rounded transition ${
                                                          isActive(menu.route)
                                                              ? "bg-gray-600"
                                                              : "hover:bg-gray-600"
                                                      }`}
                                                  >
                                                      {menu.label}
                                                  </a>
                                              </li>
                                          ))
                                        : currentUser.role === UserRole.COMPANY
                                          ? menuCompany.map((menu, i) => (
                                                <li key={i}>
                                                    <a
                                                        href={menu.route}
                                                        className={`block p-3 rounded transition ${
                                                            isActive(menu.route)
                                                                ? "bg-gray-600"
                                                                : "hover:bg-gray-600"
                                                        }`}
                                                    >
                                                        {menu.label}
                                                    </a>
                                                </li>
                                            ))
                                          : menuAdmin.map((menu, i) => (
                                                <li key={i}>
                                                    <a
                                                        href={menu.route}
                                                        className={`block p-3 rounded transition ${
                                                            isActive(menu.route)
                                                                ? "bg-gray-600"
                                                                : "hover:bg-gray-600"
                                                        }`}
                                                    >
                                                        {menu.label}
                                                    </a>
                                                </li>
                                            ))}
                                </ul>
                            </nav>
                            <button
                                type="button"
                                className="block p-3 m-4 mt-auto rounded bg-gray-500 hover:bg-gray-600 transition"
                                onClick={onLogOut}
                            >
                                Cerrar Sesión
                            </button>
                        </div>
                    </aside>

                    <div className="lg:ml-64 transition-all duration-300">
                        <div className="sticky top-0 z-10">
                            <header className="flex lg:hidden bg-[#9C9C9A] p-4 sticky top-0 z-10 justify-between items-center h-[90px]">
                                <div className="text-white pl-2 font-bold transition-opacity duration-200 hover:opacity-80 cursor-pointer">
                                    <img
                                        className="h-[60px] w-auto"
                                        src="https://www.saaf.org.ar/wp-content/uploads/2024/11/logosaaf.webp"
                                        alt="logo"
                                    />
                                </div>
                                <button
                                    className="text-white text-2xl cursor-pointer hover:text-yellow-200"
                                    onClick={toggleMenu}
                                    aria-label="Toggle menu"
                                >
                                    <Icon
                                        name={isMenuOpen ? "bx-x" : "bx-menu"}
                                    />
                                </button>
                            </header>

                            <div
                                className={`lg:hidden bg-[#9C9C9A] overflow-hidden transition-all duration-300 ${
                                    isMenuOpen ? "max-h-90" : "max-h-0"
                                }`}
                            >
                                <nav className="px-4 py-2">
                                    <ul className="space-y-2">
                                        {currentUser.role === UserRole.APPLICANT
                                            ? menuApplicant.map((menu, i) => (
                                                  <li key={i}>
                                                      <a
                                                          href={menu.route}
                                                          className={`block px-2 py-2 text-white rounded transition ${
                                                              isActive(
                                                                  menu.route,
                                                              )
                                                                  ? "bg-gray-600"
                                                                  : "hover:bg-gray-600"
                                                          }`}
                                                      >
                                                          {menu.label}
                                                      </a>
                                                  </li>
                                              ))
                                            : currentUser.role ===
                                                UserRole.COMPANY
                                              ? menuCompany.map((menu, i) => (
                                                    <li key={i}>
                                                        <a
                                                            href={menu.route}
                                                            className={`block px-2 py-2 text-white rounded transition ${
                                                                isActive(
                                                                    menu.route,
                                                                )
                                                                    ? "bg-gray-600"
                                                                    : "hover:bg-gray-600"
                                                            }`}
                                                        >
                                                            {menu.label}
                                                        </a>
                                                    </li>
                                                ))
                                              : menuAdmin.map((menu, i) => (
                                                    <li key={i}>
                                                        <a
                                                            href={menu.route}
                                                            className={`block px-2 py-2 text-white rounded transition ${
                                                                isActive(
                                                                    menu.route,
                                                                )
                                                                    ? "bg-gray-600"
                                                                    : "hover:bg-gray-600"
                                                            }`}
                                                        >
                                                            {menu.label}
                                                        </a>
                                                    </li>
                                                ))}
                                        <li className="block px-2 py-2 text-white rounded hover:bg-gray-600 transition">
                                            <button
                                                type="button"
                                                onClick={onLogOut}
                                            >
                                                Cerrar Sesión
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </div>

                        {children}
                    </div>
                </div>
            ) : (
                <div>
                    <header className="w-full bg-[#9C9C9A]">
                        <div className="w-full py-6 px-8 flex items-center">
                            <img
                                src="https://www.saaf.org.ar/wp-content/uploads/2024/11/logosaaf.webp"
                                alt="Logo"
                                className="h-12 w-auto"
                            />
                        </div>
                    </header>
                    {children}
                </div>
            )}
            {currentUser?.role !== UserRole.ADMIN && <IconContact />}

            <Footer hasSidebar={true} />
        </>
    );
}
